import React, { useEffect, useState } from 'react';
import { userService } from '../../_services';
import { apiJson } from '../../_helpers/api';
import { avatarFor } from '../helpers/source-icons';
import DashboardLayout from '../dashboard/DashboardLayout';
import ConfirmModal from '../ui/ConfirmModal';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const ROLE_LABEL = { buyer_agent: "Buyer's agent", showing_agent: 'Showing agent', both: 'Buyer\'s + showing agent' };

const LICENSE_BADGE = {
  verified: { label: 'Verified', cls: 'completed' },
  pending: { label: 'Pending review', cls: 'accepted' },
  rejected: { label: 'Rejected', cls: 'cancelled' },
  unverified: { label: 'Not submitted', cls: 'cancelled' }
};

function api(path, body) {
  return apiJson('/endpoint/users' + path, { method: 'POST', body });
}

function LicenseCard({ user, onChanged }) {
  const status = user.license_status || 'unverified';
  const badge = LICENSE_BADGE[status] || LICENSE_BADGE.unverified;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    number: (user.license && user.license.number) || '',
    state: (user.license && user.license.state) || '',
    brokerage: (user.license && user.license.brokerage) || '',
    expires: user.license && user.license.expires ? String(user.license.expires).slice(0, 10) : ''
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = () => {
    if (!form.number.trim() || !form.state) return setError('License number and state are required.');
    setBusy(true);
    setError(null);
    api('/me/license', form).then(res => {
      setBusy(false);
      if (res.status === 'success') { setEditing(false); onChanged(); }
      else setError(res.message || 'Could not submit.');
    }).catch(() => { setBusy(false); setError('Network error.'); });
  };

  return (
    <div className="sh-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Real-estate license</h3>
        <span className={'sh-status ' + badge.cls}>{badge.label}</span>
      </div>

      {status === 'pending' ? (
        <p className="sh-page-sub" style={{ margin: 0 }}>Our team is reviewing your license — usually done within one business day. You'll get an email when it's verified.</p>
      ) : null}
      {status === 'rejected' ? (
        <p className="sh-page-sub" style={{ margin: '0 0 12px' }}>Your last submission couldn't be verified — check the email we sent, correct the details, and resubmit.</p>
      ) : null}
      {status === 'verified' && !editing ? (
        <p className="sh-page-sub" style={{ margin: 0 }}>You're clear to accept showings.</p>
      ) : null}

      {user.license && user.license.number && !editing ? (
        <div className="sh-job-meta" style={{ marginTop: 10 }}>
          <span><b>Number:</b> {user.license.number}</span>
          <span><b>State:</b> {user.license.state}</span>
          {user.license.brokerage ? <span><b>Brokerage:</b> {user.license.brokerage}</span> : null}
          {user.license.expires ? <span><b>Expires:</b> {new Date(user.license.expires).toLocaleDateString()}</span> : null}
        </div>
      ) : null}

      {(status === 'pending' || status === 'verified') && !editing ? (
        <div className="sh-actions" style={{ marginTop: 14 }}>
          <button className="sh-btn sh-btn-outline" onClick={() => setEditing(true)}>
            {user.license && user.license.number ? 'Update license' : 'Edit'}
          </button>
        </div>
      ) : null}

      {(status === 'rejected' || status === 'unverified' || editing) ? (
        <div style={{ marginTop: 16 }}>
          <div className="sh-form-grid">
            <div className="sh-field">
              <label>License number</label>
              <input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} />
            </div>
            <div className="sh-field">
              <label>State</label>
              <select value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}>
                <option value="">Select…</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sh-field">
              <label>Brokerage</label>
              <input value={form.brokerage} onChange={e => setForm({ ...form, brokerage: e.target.value })} />
            </div>
            <div className="sh-field">
              <label>Expires</label>
              <input type="date" value={form.expires} onChange={e => setForm({ ...form, expires: e.target.value })} />
            </div>
          </div>
          {error ? <div className="sh-error">{error}</div> : null}
          <div className="sh-actions">
            <button className="sh-btn sh-btn-primary" disabled={busy} onClick={submit}>
              {busy ? 'Submitting…' : 'Submit for verification'}
            </button>
            {editing ? <button className="sh-btn sh-btn-outline" onClick={() => setEditing(false)}>Cancel</button> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Profile() {
  const [user, setUser] = useState(userService.currentUserValue);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const sub = userService.currentUser.subscribe(u => {
      setUser(u);
      if (u) {
        // initialize the form once — never clobber in-progress edits
        setForm(prev => prev || {
          name: u.name || '',
          user_type: u.type || 'buyer_agent',
          coverage_city: (u.coverage && u.coverage.cities && u.coverage.cities[0]) || '',
          coverage_radius: (u.coverage && u.coverage.radius_miles) || 25
        });
      }
    });
    return () => sub.unsubscribe();
  }, []);

  if (!user || !form) return <DashboardLayout><div className="sh-empty">Loading…</div></DashboardLayout>;

  const showsCoverage = form.user_type === 'showing_agent' || form.user_type === 'both';

  const save = () => {
    setBusy(true);
    setSaved(false);
    setSaveError(null);
    const payload = { name: form.name, user_type: form.user_type };
    if (showsCoverage) payload.coverage = { city: form.coverage_city, radius_miles: form.coverage_radius };
    api('/me/profile', payload).then(res => {
      setBusy(false);
      if (res.status === 'success') {
        setSaved(true);
        userService.loadUserInfo();
        setTimeout(() => setSaved(false), 2500);
      } else {
        setSaveError(res.message || 'Could not save your changes.');
      }
    }).catch(() => { setBusy(false); setSaveError('Network error — try again.'); });
  };

  const removeAccount = () => {
    userService.removeAccount().then(() => userService.logout());
  };

  return (
    <DashboardLayout>
    <div className="sh-page" style={{ padding: 0, maxWidth: 860 }}>
      <h1>My profile</h1>
      <p className="sh-page-sub">Your account, license, and coverage area.</p>

      <div className="sh-card" style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
        <img src={avatarFor(user)} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 19 }}>{user.name}</div>
          <div className="sh-job-meta">
            <span>{user.email}</span>
            {user.phone_number ? <span>{user.phone_number}</span> : null}
          </div>
          <div className="sh-job-meta" style={{ marginTop: 4 }}>
            <span><b>{ROLE_LABEL[user.type] || user.type}</b></span>
            {user.showings_completed ? <span>✓ {user.showings_completed} showings completed</span> : null}
            {user.rating ? <span>★ {Number(user.rating).toFixed(1)}</span> : null}
          </div>
        </div>
      </div>

      <LicenseCard user={user} onChanged={() => userService.loadUserInfo()} />

      <div className="sh-card">
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 800 }}>Account settings</h3>
        <div className="sh-form-grid">
          <div className="sh-field">
            <label>Full name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="sh-field">
            <label>I use the platform as</label>
            <select value={form.user_type} onChange={e => setForm({ ...form, user_type: e.target.value })}>
              <option value="buyer_agent">Buyer's agent — I post showings</option>
              <option value="showing_agent">Showing agent — I cover showings</option>
              <option value="both">Both</option>
            </select>
          </div>
          {showsCoverage ? (
            <>
              <div className="sh-field">
                <label>City I cover</label>
                <input value={form.coverage_city} onChange={e => setForm({ ...form, coverage_city: e.target.value })} placeholder="Austin" />
              </div>
              <div className="sh-field">
                <label>Travel radius</label>
                <select value={form.coverage_radius} onChange={e => setForm({ ...form, coverage_radius: e.target.value })}>
                  <option value="10">10 miles</option>
                  <option value="25">25 miles</option>
                  <option value="50">50 miles</option>
                </select>
              </div>
            </>
          ) : null}
        </div>
        {saved ? <div className="sh-success">Saved.</div> : null}
        {saveError ? <div className="sh-error">{saveError}</div> : null}
        <div className="sh-actions">
          <button className="sh-btn sh-btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      <div className="sh-card">
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>Danger zone</h3>
        <p className="sh-page-sub" style={{ marginBottom: 14 }}>Deleting your account removes your profile and cancels any open showings you posted.</p>
        <button className="sh-btn sh-btn-danger-ghost" onClick={() => setConfirmDelete(true)}>Delete my account</button>
        {confirmDelete ? (
          <ConfirmModal
            title="Delete your account?"
            message="This permanently removes your profile and cancels any open showings you posted. It cannot be undone."
            confirmLabel="Delete forever"
            danger
            onConfirm={() => { setConfirmDelete(false); removeAccount(); }}
            onClose={() => setConfirmDelete(false)}
          />
        ) : null}
      </div>
    </div>
    </DashboardLayout>
  );
}
